// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {ButtonEl} from 'common/components/button';
import {ButtonTheme, ButtonType} from 'common/components/button/constants';
import {FilterBarComplexFilterEl} from 'common/components/filter-bar/complex-filter';
import {FilterBarSimpleFilterEl} from 'common/components/filter-bar/simple-filter';
import {InputEl} from 'common/components/input';
import {InputSize} from 'common/components/input/types';
import {SelectEl} from 'common/components/select';
import {SvgIconEl} from 'common/components/svg-icon';
import {TooltipEl} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';

import {ButtonActionOption, FilterSelectConfig, SelectActionOption} from './types';
import type {FilterBar} from './index';

const SimpleActionButton = ({comp, actionOption}: {comp: FilterBar; actionOption: ButtonActionOption}) => {
  const toolTipMaxWidthStyle = actionOption.toolTipMaxWidthStyle ? actionOption.toolTipMaxWidthStyle : `150px`;

  return (
    <>
      <ButtonEl
        sel='.action'
        key={JSON.stringify(actionOption)}
        attrs={{
          theme: (comp.attr(`action-button-theme`) as ButtonTheme) || ButtonTheme.Tertiary,
          role: `button`,
          tabindex: 0,
          disabled: actionOption.disabled,
          elref: actionOption.eventName,
          type: actionOption.type as ButtonType,
          icon: actionOption.icon,
          href: actionOption.href,
          target: actionOption.target,
        }}
        on={{
          click: actionOption.disabled ? null : () => comp.helpers.handleClickAction(actionOption.eventName),
        }}
      >
        {actionOption.label}
      </ButtonEl>
      {actionOption.disabledTooltip && actionOption.disabled ? (
        <TooltipEl
          attrs={{placement: Placement.Bottom, 'max-width': toolTipMaxWidthStyle}}
          params={{text: actionOption.disabledTooltip}}
        />
      ) : (
        actionOption.tooltip && (
          <TooltipEl
            attrs={{placement: Placement.Bottom, 'max-width': toolTipMaxWidthStyle}}
            params={{text: actionOption.tooltip}}
          />
        )
      )}
    </>
  );
};

const MultipleActionButton = ({comp, actionOption}: {comp: FilterBar; actionOption: SelectActionOption}) => (
  <SelectEl
    sel='.action'
    key={JSON.stringify(actionOption)}
    attrs={{
      icon: {leftIcon: actionOption.icon},
      'selected-option': actionOption.label,
      'menu-width': actionOption.menuWidth || 200,
      'menu-height': actionOption.menuHeight || 300,
      elref: actionOption.eventName,
    }}
    props={{
      sections: actionOption.sections,
    }}
    on={{
      change: (ev: any) => comp.helpers.handleClickAction(actionOption.eventName, ev.detail.item),
    }}
  />
);

const ItemFilter = ({comp, filterSelectConfig}: {comp: FilterBar; filterSelectConfig: FilterSelectConfig}) => {
  const {print} = comp.getContext(`i18n`);
  const simpleFilterMaxWidth = `${Math.floor(100 / comp.getFilters().length) - 5}%`;

  return filterSelectConfig.filterType === comp.helpers.FilterType.Complex ? (
    <FilterBarComplexFilterEl
      attrs={{
        sections: filterSelectConfig.sections,
      }}
      on={{
        changeFilter: comp.helpers.handleFilterChanged,
        menuChange: (ev) => {
          comp.helpers.handleMenuChange(ev, filterSelectConfig.name);
        },
      }}
    />
  ) : (
    <FilterBarSimpleFilterEl
      class={{
        'filter-option': true,
        [filterSelectConfig.name]: true,
      }}
      attrs={{
        name: filterSelectConfig.name,
        multiple: filterSelectConfig.multiple,
        'apply-button': filterSelectConfig.applyButton,
        label: print(comp.helpers.getFilterSelectedLabel(filterSelectConfig)),
        'menu-width': filterSelectConfig.menuWidth || 200,
        theme: filterSelectConfig.theme,
      }}
      params={{
        sections: filterSelectConfig.sections,
      }}
      on={{
        changeFilter: comp.helpers.handleFilterChanged,
        applyFilter: comp.helpers.handleFilterApplyClicked,
        menuChange: (ev) => {
          comp.helpers.handleMenuChange(ev, filterSelectConfig.name);
        },
      }}
      style={{
        'max-width': simpleFilterMaxWidth,
      }}
    />
  );
};

export function template(this: FilterBar) {
  const {searchText} = this.state;
  const {t} = this.getContext(`i18n`);
  const actions = this.params.actions || this.attr(`actions`);

  return (
    <div sel='.filter-bar' class={{'search-first': this.attr(`search-first`)}} attrs={{part: `filter-bar-root`}}>
      {this.attr(`selected-count`) > 0 || this.attr(`always-show-count`) ? (
        <>
          <div sel='.selected-count' attrs={{elref: `selectedCount`}} on={{click: this.helpers.handleClearSelected}}>
            <div sel='.results'>{t(`components.filter-bar.x-selected`, {count: this.attr(`selected-count`)})}</div>
            {this.attr(`selected-count`) > 0 && (
              <div sel='.x-button-wrap'>
                <SvgIconEl attrs={{icon: `x`}} />
              </div>
            )}
          </div>
          <div sel='.action-section'>
            <div
              style={{
                'margin-left': `15px`,
              }}
            >
              <slot attrs={{name: `filter-select-action`}} />
            </div>
            {actions.map((actionOption) => (
              <div sel='.action-container'>
                {`sections` in actionOption && actionOption.sections.length > 0 ? (
                  <MultipleActionButton comp={this} actionOption={actionOption} />
                ) : (
                  <SimpleActionButton comp={this} actionOption={actionOption} />
                )}
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div sel='.filtered-count'>
            <div sel='.results' attrs={{elref: `resultCount`}}>
              {this.attr(`result-count`)}
            </div>
          </div>
          <div
            sel='.filter-by-text'
            class={{hidden: this.helpers.isFilterSectionHidden() || this.attr(`hide-filter-by-text`)}}
          >
            {t(`components.filter-bar.label`)}
          </div>
          <div sel='.filter-options'>
            {this.getFilters().map((filterSelectConfig) => (
              <ItemFilter comp={this} filterSelectConfig={filterSelectConfig} />
            ))}
          </div>
        </>
      )}
      <div sel='.filter-right-side'>
        {!this.attr(`hide-reset-filters`) && this.helpers.isFilterSelected() ? (
          <div sel='.reset-filter-button' on={{click: this.helpers.handleFilterReset}}>
            {t(`components.filter-bar.reset`)}
          </div>
        ) : null}
        <slot attrs={{name: `filter-actions`}} />
        {!this.attr(`hide-search`) ? (
          <InputEl
            sel='.search-input'
            attrs={{
              icon: `search`,
              placeholder: this.attr(`search-placeholder`),
              size: InputSize.Medium,
              value: searchText,
              elref: `searchInput`,
              'show-clear': true,
            }}
            style={{width: `${this.helpers.getTextWidth()}px`}}
            props={{
              maxLength: 50,
              value: searchText,
            }}
            on={{
              blur: () => this.helpers.handleInputExpanded(false),
              clear: () => this.helpers.handleUpdateInput(``),
              focusin: () => this.helpers.handleInputExpanded(true),
              input: (ev: any) => this.helpers.handleUpdateInput(ev.target.value),
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
